import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Flex,
  Form,
  Input,
  Row,
  Spin,
  UploadFile
} from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useCounterpartServiceGetActiveContractListEndpointKey } from 'src/api/queries'
import {
  CounterpartService,
  GetActiveContractListResponse,
  GetActiveContractListResponseItem
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import {
  CustomItemRender,
  requiredRules,
  Select,
  Text,
  Upload
} from 'src/components'
import { CounterPartSelect } from 'src/features/counterParts/components'
import { useFormStyles } from 'src/features/stock/styles'
import { fixedDecimal } from 'src/features/stock/utils'
import { useUser } from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'

import { ConsumablesTable } from './ConsumablesTable'
import { PharmaceuticsTable } from './PharmaceuticsTable'
import { ServicesTable } from './ServicesTable'
import { useStyles } from './styles'

type Props = {
  form: FormInstance
  onChange?: (info: UploadChangeParam<UploadFile>) => void
}

export const InputCreateForm = ({ form, onChange }: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const { styles: formStyles } = useFormStyles()
  const { userData } = useUser()

  const [isInternalInstitution, setIsInternalInstitution] = useState(true)

  const selectedDate = Form.useWatch('invoiceDate', form)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      String(userData?.currentLegalEntityId),
      useCounterpartServiceGetActiveContractListEndpointKey,
      selectedDate
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await CounterpartService.getActiveContractListEndpoint(
        String(userData?.currentLegalEntityId),
        pageParam,
        DEFAULT_PER_PAGE,
        dayjs(selectedDate).format(yearMonthDaySlash)
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetActiveContractListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options = data?.pages.flatMap(
    (page) =>
      page.data?.flatMap(
        (item: GetActiveContractListResponseItem) =>
          item?.activeContracts?.map((contract, index) => ({
            label: `${item?.activeContracts?.[index]?.counterpart?.name} (${t('organizational_structure.idno')}: ${item?.activeContracts?.[index]?.counterpart?.idno}, ${t('counterparts.contract_number')}: ${contract?.contractNumber})`,
            value: contract.id,
            isInternalInstitution: contract.counterpart?.isInternalInstitution,
            item
          })) ?? []
      ) ?? []
  )

  const showTransportInfo = Form.useWatch('showTransportInfo', form)

  const medicineItems = Form.useWatch('medicine', form)
  const pharmaceuticsItems = Form.useWatch('pharmaceutics', form)

  const servicesItems = Form.useWatch('services', form)

  const totalMedicine = fixedDecimal(
    medicineItems?.reduce(
      (acc: number, item: { priceTotal: number }) =>
        acc + (item?.priceTotal ?? 0),
      0
    ) ?? 0,
    4
  )

  const totalPharmaceutics = fixedDecimal(
    pharmaceuticsItems?.reduce(
      (acc: number, item: { priceTotal: number }) =>
        acc + (item?.priceTotal ?? 0),
      0
    ) ?? 0,
    4
  )

  const totalServices = fixedDecimal(
    servicesItems?.reduce(
      (acc: number, item: { unitPrice: number }) =>
        acc + (item?.unitPrice ?? 0),
      0
    ) ?? 0,
    4
  )

  const totalAmount = fixedDecimal(
    (totalMedicine ?? 0) + (totalServices ?? 0),
    4
  )

  return (
    <Flex vertical gap={24} className={formStyles.form}>
      <Card>
        <Flex vertical gap={24}>
          <Flex justify="space-between" align="center">
            <Text weight={700} size="xxl">
              {t('stock_administration.record_incoming_invoice')}
            </Text>
          </Flex>

          <Card className={styles.detailsCard}>
            <Flex vertical gap={24}>
              <Form.Item shouldUpdate>
                {({ getFieldValue }) => {
                  const isContractDisabled =
                    !getFieldValue('invoiceDate') ||
                    getFieldValue('medicine')?.length > 0 ||
                    getFieldValue('consumables')?.length > 0

                  const isDisabled =
                    !getFieldValue('invoiceDate') ||
                    !getFieldValue('contractId')

                  const isDeliveryDateDisabled =
                    !getFieldValue('invoiceDate') ||
                    !getFieldValue('takeoverDate')

                  return (
                    <Row gutter={[24, 16]}>
                      <Col span={12}>
                        <Form.Item
                          name="invoiceDate"
                          label={t(`stock.invoice_date`)}
                          rules={requiredRules}
                        >
                          <DatePicker
                            className="full-width"
                            format={dayMonthYearBackSlash}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name="invoiceNumber"
                          label={t(`stock.accompanying_invoice`)}
                          rules={requiredRules}
                        >
                          <Input
                            className="full-width"
                            disabled={isDisabled}
                            placeholder={t(`stock.accompanying_invoice`)}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name="contractId"
                          label={t(`stock.provider`)}
                          rules={requiredRules}
                        >
                          <Select
                            popupMatchSelectWidth={true}
                            filterOption={false}
                            disabled={isContractDisabled}
                            options={options}
                            loading={isFetching}
                            placeholder={t('counterparts.contract_number')}
                            showSearch={true}
                            onSelect={(value) => {
                              const selectedContract = options?.find(
                                (option) => option.value === value
                              )

                              setIsInternalInstitution(
                                selectedContract?.isInternalInstitution ?? true
                              )

                              form.setFieldsValue({
                                purchase_center:
                                  selectedContract?.item?.beneficiary?.name
                              })
                            }}
                            onPopupScroll={(e) => {
                              const target = e.target as HTMLElement
                              if (
                                target.scrollTop + target.offsetHeight ===
                                target.scrollHeight
                              )
                                fetchNextPage()
                            }}
                            notFoundContent={
                              isFetching ? (
                                <Flex justify="center">
                                  <Spin />
                                </Flex>
                              ) : (
                                <Empty />
                              )
                            }
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name="purchase_center"
                          label={t(`counterparts.purchase_center`)}
                          rules={requiredRules}
                        >
                          <Input
                            className="full-width"
                            disabled
                            placeholder={t(`counterparts.purchase_center`)}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name="takeoverDate"
                          label={t(`stock_administration.pickup_date`)}
                          rules={requiredRules}
                        >
                          <DatePicker
                            className="full-width"
                            disabled={isDisabled}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name="deliveryDate"
                          label={t(`stock_administration.delivery_date`)}
                        >
                          <DatePicker
                            disabledDate={(current) =>
                              current &&
                              current <
                                dayjs(form.getFieldValue('takeoverDate'))
                            }
                            className="full-width"
                            disabled={isDeliveryDateDisabled}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )
                }}
              </Form.Item>

              <Card>
                <Flex vertical gap={24}>
                  <Form.Item name="showTransportInfo" valuePropName="checked">
                    <Checkbox>
                      {t('stock_administration.register_transport')}{' '}
                    </Checkbox>
                  </Form.Item>

                  {showTransportInfo && (
                    <Row gutter={[24, 16]}>
                      <Col span={12}>
                        <Form.Item
                          name="transporterId"
                          label={t(`stock_administration.select_transport`)}
                          rules={requiredRules}
                        >
                          <CounterPartSelect
                            allowClear={false}
                            placeholder={t(
                              `stock_administration.select_transport`
                            )}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={12}>
                        <Form.Item
                          name="transporterAccompanyingNotice"
                          label={t(`stock_administration.way_bill`)}
                          rules={requiredRules}
                        >
                          <Input
                            className="full-width"
                            placeholder={t(`stock_administration.way_bill`)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </Flex>
              </Card>
            </Flex>
          </Card>
        </Flex>
      </Card>

      <Card>
        <Form.List name="medicine">
          {(fields, { add, remove }) => (
            <ConsumablesTable
              fields={fields}
              add={add}
              remove={remove}
              form={form}
            />
          )}
        </Form.List>
        <Flex justify="end" align="center" gap={8}>
          <Text weight={700}>{t('common.total')}: </Text>
          <Text weight={700}>{totalMedicine} MDL</Text>
        </Flex>
      </Card>

      <Card>
        <Form.List name="consumables">
          {(fields, { add, remove }) => (
            <PharmaceuticsTable
              fields={fields}
              add={add}
              remove={remove}
              form={form}
            />
          )}
        </Form.List>
        <Flex justify="end" align="center" gap={8}>
          <Text weight={700}>{t('common.total')}: </Text>
          <Text weight={700}>{totalPharmaceutics} MDL</Text>
        </Flex>
      </Card>

      {!isInternalInstitution && (
        <Card>
          <Form.List name="services">
            {(fields, { add, remove }) => (
              <ServicesTable
                fields={fields}
                add={add}
                remove={remove}
                form={form}
              />
            )}
          </Form.List>
          <Flex justify="end" align="center" gap={8}>
            <Text weight={700}>
              {t('common.total')} ({t('stock.services')}):
            </Text>
            <Text weight={700}>{totalServices ?? 0} MDL</Text>
          </Flex>
          <Flex justify="end" align="center" gap={8}>
            <Text weight={700}>
              {t('common.total')} ({t('stock.services_products')}):
            </Text>
            <Text weight={700}>{totalAmount} MDL</Text>
          </Flex>
        </Card>
      )}

      <Card>
        <Flex vertical gap={24}>
          <Card title={t('stock_applications.uploaded_documents')}>
            <Form.Item name="docs" className="full-width">
              <Upload
                onChange={onChange}
                customRequest={() => null}
                itemRender={(_, file, __, { remove }) => (
                  <CustomItemRender file={file} onRemove={remove} />
                )}
              />
            </Form.Item>
          </Card>

          <Card title={t('common.comment')}>
            <Form.Item
              name="comment"
              className="full-width"
              label={t('common.comment')}
            >
              <Input.TextArea
                rows={5}
                placeholder={t('common.leave_comment')}
              />
            </Form.Item>
          </Card>
        </Flex>
      </Card>
    </Flex>
  )
}
